import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Bell, Menu } from 'lucide-react';
import { ActionUsageMeter } from '@/components/modules/ActionUsageMeter';
import { NotificationPanel } from '@/components/modules/NotificationPanel';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useCommandStore } from '@/stores/commandStore';
import { useNotificationStore } from '@/stores/notificationStore';

const POLL_INTERVAL_MS = 30_000;

const routeLabels: Record<string, string> = {
  '/': 'Command Center',
  '/today': 'Today',
  '/inbox': 'Inbox',
  '/contacts': 'Contacts',
  '/email': 'Email Ops',
  '/support': 'Support',
  '/finance': 'Finance',
  '/partnerships': 'Partnerships',
  '/growth': 'Growth Engine',
  '/messaging': 'Messaging Hub',
  '/voice': 'Voice Ops',
  '/context': 'Context Explorer',
  '/settings': 'Settings',
  '/audit': 'Audit Log',
};

interface TopBarProps {
  onMenuToggle?: () => void;
}

export function TopBar({ onMenuToggle }: TopBarProps) {
  const location = useLocation();
  const { stats } = useDashboardStore();
  const toggleCommand = useCommandStore((s) => s.toggle);
  const { unreadCount, fetch: fetchNotifications } = useNotificationStore();
  const [panelOpen, setPanelOpen] = useState(false);

  const basePath = '/' + (location.pathname.split('/')[1] || '');
  const pageTitle = routeLabels[basePath] || 'Nexus';

  // Fetch notifications on mount + poll every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const togglePanel = useCallback(() => {
    setPanelOpen((prev) => !prev);
  }, []);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
  }, []);

  return (
    <header className="h-14 bg-surface-900 border-b border-surface-700 flex items-center justify-between px-3 md:px-6 flex-shrink-0">
      {/* Left side: hamburger + breadcrumb */}
      <div className="flex items-center gap-2">
        {/* Hamburger — mobile only */}
        <button
          onClick={onMenuToggle}
          className="md:hidden p-2 -ml-1 rounded-lg text-surface-400 hover:text-surface-200 hover:bg-surface-800 transition-colors"
          title="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        <span className="text-xs text-surface-500 hidden sm:inline">Nexus</span>
        <span className="text-xs text-surface-600 hidden sm:inline">/</span>
        <span className="text-sm font-medium text-surface-200">{pageTitle}</span>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2 md:gap-4">
        {/* Command palette trigger — full version on md+, icon-only on mobile */}
        <button
          onClick={toggleCommand}
          className="hidden md:flex items-center gap-2 w-64 px-3 py-1.5 bg-surface-800 border border-surface-700 rounded-lg text-xs text-surface-500 hover:border-surface-600 hover:text-surface-400 transition-colors"
        >
          <Search className="h-3.5 w-3.5" />
          <span>Search or jump to...</span>
          <kbd className="ml-auto rounded bg-surface-700 px-1.5 py-0.5 text-[10px] font-medium text-surface-400">
            Ctrl K
          </kbd>
        </button>
        <button
          onClick={toggleCommand}
          className="md:hidden p-2 rounded-lg text-surface-400 hover:text-surface-200 hover:bg-surface-800 transition-colors"
          title="Search"
        >
          <Search className="h-4 w-4" />
        </button>

        {/* Action usage */}
        <div className="hidden lg:block w-40">
          <ActionUsageMeter used={stats?.actionsUsed ?? 0} limit={stats?.actionLimit ?? 1} />
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={togglePanel}
            className="relative p-2 rounded-lg text-surface-400 hover:text-surface-200 hover:bg-surface-800 transition-colors"
            title="Notifications"
          >
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-500 px-1 text-[10px] font-bold text-white leading-none">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          <NotificationPanel open={panelOpen} onClose={closePanel} />
        </div>
      </div>
    </header>
  );
}
